import { Add, Delete, Edit, MapRounded } from '@mui/icons-material';
import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useState, useMemo } from 'react';
import { Address } from '../../types/Address';
import { UserSettingsCollapseKeys } from '../../types/User';
import ResponsiveButton from '../../uikit/buttons/ResponsiveButton';
import ConfirmationModal from '../ConfirmationModal';
import AccountAddressModal from './AccountAddressModal';
import { useGetOrganizationIntegrations } from '../../services/react-query/integrations/useGetOrganizationIntegrations';
import { CalendarPartner } from '../../types/Integration';
import AccordionSection from '../AccordionSection';

type Props = {
  addresses: Address[];
  onAddressesChange: (addresses: Address[]) => void;
};

const AccountAddressTable: React.FC<Props> = ({ addresses, onAddressesChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<Address | null>(null);
  const [uuidToDelete, setUuidToDelete] = useState<string | null>(null);
  const { data: integrationResponse } = useGetOrganizationIntegrations({});

  // TODO: This is a temporary solution to get the synced partners to inform the user
  // we should do a follow up ticket: call backend and retrieve associated events for the address,
  // also if user still want to remove then we may wanna update event integrations too
  const syncedPartners = useMemo(() => {
    return _.map(
      _.filter(integrationResponse?.data || [], (integration) => [CalendarPartner.GOOGLE, CalendarPartner.OUTLOOK].includes(integration.partner)),
      'partner'
    );
  }, [integrationResponse]);

  const getDeleteConfirmationText = (): React.ReactNode => {
    const baseMessage = <Typography>Are you sure you want to delete this address?</Typography>;

    if (_.isEmpty(syncedPartners)) return baseMessage;

    const partnerNames = syncedPartners.map(_.capitalize).join(', ');
    return (
      <>
        {baseMessage}
        <br />
        <Typography fontWeight="bold">
          Note: If this address is linked to events, removing it will also remove it from those events. If those events are being synced, their addresses will
          not be synced after deletion to the following integrations: {partnerNames}
        </Typography>
      </>
    );
  };

  const handleAddOrUpdate = (newAddress: Address) => {
    const updated = addressToEdit ? _.map(addresses, (addr) => (addr.uuid === addressToEdit.uuid ? newAddress : addr)) : [...addresses, newAddress];

    onAddressesChange(updated);
    setAddressToEdit(null);
    setIsModalOpen(false);
  };

  const handleConfirmDelete = () => {
    if (uuidToDelete) {
      const updated = addresses.filter((addr) => addr.uuid !== uuidToDelete);
      onAddressesChange(updated);
    }
    setUuidToDelete(null);
  };

  const addressTableHeaderElements = (
    <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setIsModalOpen(true)}>
      Add
    </ResponsiveButton>
  );

  return (
    <AccordionSection
      title="Address Information"
      collapseId={UserSettingsCollapseKeys.ACCOUNT_ITEM_ADDRESS_INFORMATION}
      headerElements={addressTableHeaderElements}
      hasData={addresses.length > 0}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Address Name</TableCell>
              <TableCell>Address 1</TableCell>
              <TableCell>Address 2</TableCell>
              <TableCell>Province/State</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Postal Code</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {_.map(addresses, (address) => {
              const { type_value, address_1, address_2, division, city, country, postal_code, uuid } = address;
              const query = [address_1, city].filter(Boolean).join(', ');
              const mapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

              return (
                <TableRow key={uuid}>
                  <TableCell>{type_value}</TableCell>
                  <TableCell>{address_1}</TableCell>
                  <TableCell>{address_2}</TableCell>
                  <TableCell>{division}</TableCell>
                  <TableCell>{city}</TableCell>
                  <TableCell>{country}</TableCell>
                  <TableCell>{postal_code}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setAddressToEdit(address);
                        setIsModalOpen(true);
                      }}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => setUuidToDelete(uuid)} size="small">
                      <Delete />
                    </IconButton>
                    <IconButton href={mapsLink} target="_blank" size="small">
                      <MapRounded />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmationModal text={getDeleteConfirmationText()} isOpen={!!uuidToDelete} onCancel={() => setUuidToDelete(null)} onConfirm={handleConfirmDelete} />

      <AccountAddressModal
        isOpen={isModalOpen}
        onSave={handleAddOrUpdate}
        addressToEdit={addressToEdit}
        onCancel={() => {
          setIsModalOpen(false);
          setAddressToEdit(null);
        }}
      />
    </AccordionSection>
  );
};

export default AccountAddressTable;
